<script setup>
import { computed } from 'vue'
import { useShipApiStore } from '@/stores/shipApi'
import { storeToRefs } from 'pinia/dist/pinia'
import {
  AFTERWARD_PARALLEL_BODY_LENGTH,
  BALLASTED_DRAUGHT,
  BREADTH,
  DEPTH_FORECASTLE_DECK_TO_KEEL,
  DEPTH_MAIN_DECK_TO_KEEL,
  DEPTH_POOP_DECK_TO_KEEL,
  FORWARD_PARALLEL_BODY_LENGTH,
  LADEN_DRAUGHT,
  LENGTH_OVERALL
} from '@/constants/shipFieldConstants'
import ResetField from '@/components/common/ResetField.vue'

const store = useShipApiStore()

const props = defineProps({
  item: { type: Object, required: true }
})

const { selectedItems, currentItem, fields } = storeToRefs(useShipApiStore())

const selected = computed({
  get() {
    return !!selectedItems.value.includes(props.item)
  },
  set(value) {
    store.updateSelectedItems(value, [props.item])
  }
})

function setCurrentShip() {
  store.setCurrentItem(props.item)
}

function resetValue(property) {
  const originalProperty = `original_${property}`
  store.setParameter(props.item, property, props.item[originalProperty])
  store.handleScaling(props.item)
}

const isSelectedItem = computed(() => {
  return currentItem.value.id === props.item.id
})

const validationStatus = computed(() => {
  return store.getValidationStatus(props.item)
})

const changeLengthOverall = (value) => {
  store.setParameter(props.item, LENGTH_OVERALL, value)
  store.handleScaling(props.item)
}

const changeBreadth = (value) => {
  store.setParameter(props.item, BREADTH, value)
  store.handleScaling(props.item)
}
</script>

<template>
  <tr
    v-if="fields"
    v-ripple
    :class="{ 'selected-item': isSelectedItem, clickable: true }"
    @click="setCurrentShip()"
  >
    <td class="pl-4 pr-0" style="min-width: 40px">
      <v-checkbox v-model="selected" density="compact" hide-details @click.stop=""></v-checkbox>
    </td>
    <v-tooltip :text="item.name">
      <template #activator="{ props }">
        <td v-bind="props" style="max-width: 120px" class="pr-0 pl-1 name-cell">
          {{ item.name }}
        </td>
      </template>
    </v-tooltip>
    <td class="text-center">{{ item.capacity }}</td>
    <td class="border-left-grey text-center pr-0 pl-2">
      <ResetField
        :field-name="LENGTH_OVERALL"
        :item="item"
        @change="changeLengthOverall"
        @resetValue="resetValue(LENGTH_OVERALL)"
      />
    </td>
    <td class="text-center pr-1 pl-2">
      <ResetField
        :field-name="BREADTH"
        :item="item"
        @change="changeBreadth"
        @resetValue="resetValue(BREADTH)"
      />
    </td>
    <td class="border-left-grey text-center pr-0 pl-2">
      <ResetField
        :field-name="LADEN_DRAUGHT"
        :item="item"
        @change="store.setParameter(item, LADEN_DRAUGHT, $event)"
        @resetValue="resetValue(LADEN_DRAUGHT)"
      />
    </td>
    <td class="pr-1 pl-3">
      <ResetField
        :field-name="BALLASTED_DRAUGHT"
        :item="item"
        @change="store.setParameter(item, BALLASTED_DRAUGHT, $event)"
        @resetValue="resetValue(BALLASTED_DRAUGHT)"
      />
    </td>
    <td class="border-left-grey text-center pr-0 pl-2">
      <ResetField
        :field-name="DEPTH_MAIN_DECK_TO_KEEL"
        :item="item"
        @change="store.setParameter(item, DEPTH_MAIN_DECK_TO_KEEL, $event)"
        @resetValue="resetValue(DEPTH_MAIN_DECK_TO_KEEL)"
      />
    </td>
    <td class="text-center pr-1 pl-3">
      <ResetField
        :field-name="DEPTH_FORECASTLE_DECK_TO_KEEL"
        :item="item"
        @change="store.setParameter(item, DEPTH_FORECASTLE_DECK_TO_KEEL, $event)"
        @resetValue="resetValue(DEPTH_FORECASTLE_DECK_TO_KEEL)"
      />
    </td>
    <td class="text-center pr-1 pl-3">
      <ResetField
        :field-name="DEPTH_POOP_DECK_TO_KEEL"
        :item="item"
        @change="store.setParameter(item, DEPTH_POOP_DECK_TO_KEEL, $event)"
        @resetValue="resetValue(DEPTH_POOP_DECK_TO_KEEL)"
      />
    </td>
    <td class="border-left-grey text-center">
      {{ item[AFTERWARD_PARALLEL_BODY_LENGTH] }}
    </td>
    <td>
      {{ item[FORWARD_PARALLEL_BODY_LENGTH] }}
    </td>
    <td class="border-left-grey text-center">
      <v-icon v-if="validationStatus" color="green">mdi-check-circle</v-icon>
    </td>
  </tr>
</template>
<style scoped>
.name-cell {
  width: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
